import React from "react";
import { icon, number } from "../../../../config";
import { truncateName } from "../../../../helper/common";

/**
 * Component for rendering queue breadcrumb item
 * @author Muskan Thakur
 */
const QueueBreadcrumbItem = ({ queueName, onQueueClick, queueClickRef }) => {
  if (!queueName) return null;

  return (
    <>
      <span ref={queueClickRef} className="link-text cursor-pointer" onClick={onQueueClick} title={queueName}>
        {truncateName(queueName, number.THIRTY)}
      </span>
      {icon.RIGHT_BREADCRUMB}
    </>
  );
};

/**
 * Component for rendering project breadcrumb item
 * @author Muskan Thakur
 */
const ProjectBreadcrumbItem = ({ project, onProjectClick, projectClickRef }) => {
  if (!project) return null;

  return (
    <>
      <span ref={projectClickRef} className="link-text cursor-pointer" onClick={onProjectClick} title={project}>
        {truncateName(project, number.THIRTY)}
      </span>
      {icon.RIGHT_BREADCRUMB}
    </>
  );
};

/**
 * Component for rendering automation detail breadcrumb (when automationId exists)
 * @author Muskan Thakur
 */
const AutomationDetailBreadcrumb = ({ automationId, selectedAutomation, automations, onAutomationClick }) => {
  const automationName =
    selectedAutomation?.id?.toString() === automationId?.toString()
      ? selectedAutomation?.name
      : automations?.find((auto) => auto.id?.toString() === automationId?.toString())?.name || "Automation";

  return (
    <>
      <span className="link-text cursor-pointer" onClick={onAutomationClick}>
        {"Automation"}
      </span>
      {icon.RIGHT_BREADCRUMB}
      <span className="cursor-text">{automationName}</span>
    </>
  );
};

/**
 * Component for rendering automation list breadcrumb (when automationId doesn't exist)
 * @author Muskan Thakur
 */
const AutomationListBreadcrumb = ({ onAutomationClick }) => {
  return (
    <span className="link-text cursor-pointer" onClick={onAutomationClick}>
      {"Automations"}
    </span>
  );
};

/**
 * Main component for rendering automation breadcrumbs
 * Handles different conditions: queue, project, and automation detail/list views
 * @author Muskan Thakur
 */
const AutomationBreadcrumb = ({
  queueName,
  project,
  automationId,
  selectedAutomation,
  automations,
  onQueueClick,
  onProjectClick,
  onAutomationClick,
  queueClickRef,
  projectClickRef,
}) => {
  return (
    <>
      <QueueBreadcrumbItem queueName={queueName} onQueueClick={onQueueClick} queueClickRef={queueClickRef} />
      {project && (
        <>
          <ProjectBreadcrumbItem project={project} onProjectClick={onProjectClick} projectClickRef={projectClickRef} />
          {automationId ? (
            <AutomationDetailBreadcrumb
              automationId={automationId}
              selectedAutomation={selectedAutomation}
              automations={automations}
              onAutomationClick={onAutomationClick}
            />
          ) : (
            <AutomationListBreadcrumb onAutomationClick={onAutomationClick} />
          )}
        </>
      )}
    </>
  );
};

export default AutomationBreadcrumb;
